import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { Layout, Typography, Table, Col } from "antd";

import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";

import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";

import Comment from "components/Comments";

import CiResultDisplay from "./CiResultDisplay";

import { transformDpatTableRows, allGroupedMeetingLinksUploaded, isEvidenceLinkUploaded } from "utils/renderDpatEvidenceLink";

import { countUniqueSubStructureMeetings, getYearFromDateValue } from "utils/utils";



const { Title, Text } = Typography;



function isEstablishedBySelectedYear(establishmentDate, selectedYear) {

  const yearNum = Number(selectedYear);

  if (!Number.isFinite(yearNum)) {

    return false;

  }



  const establishedYear = getYearFromDateValue(establishmentDate);

  return establishedYear != null && establishedYear <= yearNum;

}



const SubStructureMeeting = forwardRef(({

  data,

  columns,

  establishment,

  establishmentColumns,

  revenueSharing,

  revenuSharingColumns,

  districtId,

  year,

  hideComment,

  savedAssessmentData,

  isAssessmentStarted,

}, ref) => {

  const resolved = useMemo(() => {

    if (!isAssessmentStarted || !savedAssessmentData) {

      return { data, establishment, revenueSharing };

    }

    return {

      data: savedAssessmentData.data ?? data,

      establishment: savedAssessmentData.establishment ?? establishment,

      revenueSharing: savedAssessmentData.revenueSharing ?? revenueSharing,

      fulfillment: savedAssessmentData.fulfillment,

    };

  }, [isAssessmentStarted, savedAssessmentData, data, establishment, revenueSharing]);



  const filteredEstablishment = useMemo(() => {

    const rows = resolved.establishment || [];

    return rows.filter((item) => isEstablishedBySelectedYear(item?.date, year));

  }, [resolved.establishment, year]);



  const filteredRevenueSharing = useMemo(() => {

    const revenue = resolved.revenueSharing;

    if (!revenue) {

      return revenue;

    }



    const allowedNames = new Set(

      filteredEstablishment

        .map((item) => String(item?.name || "").trim().toLowerCase())

        .filter(Boolean),

    );



    const rows = revenue.data || [];

    const filteredRows = allowedNames.size > 0

      ? rows.filter((row) => {

          const rawName = row?.name && typeof row.name === "object" && row.name?.props?.children != null

            ? row.name.props.children

            : row?.name;

          return allowedNames.has(String(rawName || "").trim().toLowerCase());

        })

      : [];



    return {

      ...revenue,

      data: filteredRows,

    };

  }, [resolved.revenueSharing, filteredEstablishment]);



  const [fulfillment, setFulfillment] = useState(

    resolved.fulfillment || resolved.data?.fulfillment,

  );



  const MEETING_LINK_KEYS = ["firstLink", "secondLink", "thirdLink"];

  const MEETING_LINK_LABELS = {

    firstLink: "View Minutes",

    secondLink: "View Minutes",

    thirdLink: "View Minutes",

  };



  const transformSubStructureData = (dataArray) =>

    transformDpatTableRows(dataArray, MEETING_LINK_KEYS, MEETING_LINK_LABELS);



  const transformEstablishmentData = (dataArray) => {

    if (!dataArray) return [];

    return dataArray.map((item) => ({ ...item }));

  };



  const transformRevenueSharingData = (dataArray) => {

    if (!dataArray) return [];

    return dataArray.map((item) => ({

      ...item,

      name: item.name && typeof item.name === "object" && item.name?.props?.children ? (

        item.name.props.children

      ) : item.name,

      collected: item.collected && typeof item.collected === "object" && item.collected?.props?.children != null ? (

        item.collected.props.children

      ) : item.collected,

      ceded: item.ceded && typeof item.ceded === "object" && item.ceded?.props?.children != null ? (

        item.ceded.props.children

      ) : item.ceded,

      percentage: item.percentage && typeof item.percentage === "object" && item.percentage?.props?.children != null ? (

        item.percentage.props.children

      ) : item.percentage,

    }));

  };



  useEffect(() => {

    if (!resolved.data) {

      setFulfillment("Not Fulfilled");

      return;

    }



    let computedFulfillment = resolved.fulfillment || resolved.data.fulfillment || "Not Fulfilled";

    const uniqueSubMeetings = countUniqueSubStructureMeetings(resolved.data.subMeeting);

    const establishmentCount = filteredEstablishment.length;

    if (

      computedFulfillment === "Fulfilled" &&

      uniqueSubMeetings > 0 &&

      uniqueSubMeetings > establishmentCount

    ) {

      computedFulfillment = "Not Fulfilled";

    }

    if (filteredRevenueSharing?.fulfillment === "Not Fulfilled") {

      computedFulfillment = "Not Fulfilled";

    }



    const subMeetingRows = resolved.data?.subMeeting || [];

    if (subMeetingRows.length > 0 && !subMeetingRows.every((row) => allGroupedMeetingLinksUploaded(row))) {

      computedFulfillment = "Not Fulfilled";

    }



    const revenueRows = filteredRevenueSharing?.data || [];

    if (revenueRows.some((row) => row?.name && !isEvidenceLinkUploaded(row.docs))) {

      computedFulfillment = "Not Fulfilled";

    }



    setFulfillment(computedFulfillment);

  }, [resolved, filteredEstablishment, filteredRevenueSharing]);



  useImperativeHandle(ref, () => ({

    getData: () => ({

      indicator: "CI1",

      area: "General Assembly Meetings and Approvals",

      data: resolved.data,

      establishment: filteredEstablishment,

      revenueSharing: filteredRevenueSharing,

      fulfillment,

    }),

  }));



  const DPAT_SLUG = "c1.0-1.3";



  return (

    <Comment

      data={resolved.data}

      year={year}

      districtId={districtId}

      tableCommentedId={`c1.0-1.3-${year}`}

      hideComment={hideComment}

    >

      {({ renderCommentFeedbackFooter }) => (

        <DpatSubIndicatorSection code={DPAT_SLUG}>

          <DpatIndicatorCmsIntro year={year} slug={DPAT_SLUG} />



          <Col align="start">

            <CiResultDisplay slug={DPAT_SLUG} fulfillment={fulfillment} />

          </Col>



          <Title level={4} style={{ marginTop: "20px" }}>Evidence of meetings of sub-structures prior to Annual General Meeting</Title>

          {resolved.data?.data ? (

            <Table

              columns={columns}

              dataSource={transformSubStructureData(

                resolved.data.data.concat(resolved.data.subMeeting || []),

              )}

              pagination={false}

              bordered

              rowKey={(record, index) => `${record.key || index}`}

            />

          ) : (

            <Text>No substructure meeting data available</Text>

          )}



          <Title level={4} style={{ marginTop: "20px" }}>Evidence of establishment of sub-structures</Title>

          {filteredEstablishment.length > 0 ? (

            <Table

              columns={establishmentColumns}

              dataSource={transformEstablishmentData(filteredEstablishment)}

              pagination={false}

              bordered

              rowKey={(record, index) => `${record.key || index}`}

            />

          ) : (

            <Text>No establishment data available</Text>

          )}



          <Title level={4} style={{ marginTop: "20px" }}>Evidence of revenue sharing</Title>

          {filteredRevenueSharing?.data?.length > 0 ? (

            <Table

              columns={revenuSharingColumns}

              dataSource={transformRevenueSharingData(filteredRevenueSharing.data)}

              pagination={false}

              bordered

              rowKey={(record, index) => `${index}`}

            />

          ) : (

            <Text>No revenue sharing data available</Text>

          )}

          {!hideComment && renderCommentFeedbackFooter()}

        </DpatSubIndicatorSection>

      )}

    </Comment>

  );

});



export default SubStructureMeeting;


